import React, { useCallback, useEffect, useRef, useState } from 'react';

// A content-agnostic split/compare shell. Each child is one pane (a
// GradedCanvas, a <video>, a crop <canvas>, etc.). Panes are laid out
// side-by-side (draggable divider for 2 panes), as a wipe (clip-path), or as a
// difference (mix-blend-mode). Wipe/diff only apply to 2-pane comparisons.
//
// Props:
//   panes:      [{ label, color }] metadata aligned to children (optional)
//   children:   one node per pane
//   defaultMode: 'side' | 'wipe' | 'diff'
//   controls:   show the built-in control row (default true)
export default function SplitPreview({ panes = [], children, defaultMode = 'side', controls = true, className, style }) {
  const items = React.Children.toArray(children);
  const n = items.length;
  const twoUp = n === 2;

  const [mode, setMode] = useState(defaultMode);
  const [ratio, setRatio] = useState(0.5); // side: width of first pane
  const [wipe, setWipe] = useState(0.5); // wipe: clip position of top pane
  const [swapped, setSwapped] = useState(false);

  const bodyRef = useRef(null);
  const dragRef = useRef(null); // 'divider' | 'wipe' | null

  const effectiveMode = twoUp ? mode : 'side';

  const order = swapped && twoUp ? [1, 0] : items.map((_, i) => i);
  const meta = (i) => panes[i] || {};

  const onPointerMove = useCallback((e) => {
    const kind = dragRef.current;
    if (!kind || !bodyRef.current) return;
    const rect = bodyRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    if (kind === 'divider') setRatio(Math.max(0.12, Math.min(0.88, x)));
    else if (kind === 'wipe') setWipe(x);
  }, []);

  const stopDrag = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', stopDrag);
  }, [onPointerMove]);

  const startDrag = (kind) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = kind;
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', stopDrag);
  };

  useEffect(() => stopDrag, [stopDrag]);

  // Render a pane as plain JSX (NOT a nested component). Defining a component
  // inside render gives it a new type identity every render, which makes React
  // unmount + remount the whole subtree — for a GradedCanvas that means tearing
  // down and re-initializing WebGL on every parent re-render (~12fps from the
  // playhead), i.e. a constantly blinking preview. A function call keeps the
  // children mounted and lets React reconcile by position.
  const renderPane = (idx, extraClass, extraStyle, key) => {
    const m = meta(idx);
    return (
      <div
        key={key}
        className={`cs-split-pane ${extraClass || ''}`}
        style={{ ...(m.color ? { borderTopColor: m.color } : {}), ...extraStyle }}
      >
        {m.label && <span className="cs-split-label cs-badge">{m.label}</span>}
        <div className="cs-split-content">{items[idx]}</div>
      </div>
    );
  };

  let body;
  if (effectiveMode === 'side') {
    body = (
      <div className="cs-split-body cs-split-side" ref={bodyRef}>
        {twoUp ? (
          <>
            {renderPane(order[0], '', { flex: `0 0 ${ratio * 100}%` })}
            <div className="cs-split-divider" onPointerDown={startDrag('divider')} title="Drag to resize" />
            {renderPane(order[1], '', { flex: '1 1 0' })}
          </>
        ) : (
          items.map((_, i) => renderPane(i, '', { flex: '1 1 0' }, i))
        )}
      </div>
    );
  } else if (effectiveMode === 'wipe') {
    body = (
      <div className="cs-split-body cs-split-overlay" ref={bodyRef}>
        {renderPane(order[0], 'cs-split-base')}
        {renderPane(order[1], 'cs-split-top', { clipPath: `inset(0 0 0 ${wipe * 100}%)` })}
        <div className="cs-split-wipe-handle" style={{ left: `${wipe * 100}%` }} onPointerDown={startDrag('wipe')} />
      </div>
    );
  } else {
    body = (
      <div className="cs-split-body cs-split-overlay" ref={bodyRef}>
        {renderPane(order[0], 'cs-split-base')}
        {renderPane(order[1], 'cs-split-top cs-split-diff')}
      </div>
    );
  }

  return (
    <div className={`cs-split ${className || ''}`} style={style}>
      {controls && (
        <div className="cs-split-controls">
          {twoUp && (
            <div className="cs-segment cs-segment-sm">
              <button className={mode === 'side' ? 'on' : ''} onClick={() => setMode('side')}>Side</button>
              <button className={mode === 'wipe' ? 'on' : ''} onClick={() => setMode('wipe')}>Wipe</button>
              <button className={mode === 'diff' ? 'on' : ''} onClick={() => setMode('diff')}>Diff</button>
            </div>
          )}
          {twoUp && (
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setSwapped((s) => !s)} title="Swap sides">
              ⇄ Swap
            </button>
          )}
        </div>
      )}
      {body}
    </div>
  );
}
